import { getCountryDataList } from 'countries-list'

/**
 * Split a combined phone string ("+44 7911 123456") into its dial code and
 * national number. Falls back to '+1' when no code is present — intended for
 * the edit drawers, NOT for display (see getPhoneDisplay).
 */
export function parsePhoneNumber(phone: string): { countryCode: string; number: string } {
  if (!phone) return { countryCode: '+1', number: '' }
  const match = phone.match(/^(\+\d+(?:-\d+)?)\s*(.*)$/)
  if (match) {
    return { countryCode: match[1], number: match[2] }
  }
  return { countryCode: '+1', number: phone }
}

export function formatPhoneNumber(countryCode: string, number: string): string {
  if (!number) return ''
  return `${countryCode} ${number}`.trim()
}

// Canadian area codes (CNAC). +1 is shared across the North American
// Numbering Plan; the area code — not the dial code — identifies the country.
const CANADIAN_AREA_CODES = new Set([
  '204', '226', '236', '249', '250', '257', '263', '289', '306', '343',
  '354', '365', '367', '368', '382', '387', '403', '416', '418', '428',
  '431', '437', '438', '450', '460', '468', '474', '506', '514', '519',
  '537', '548', '568', '579', '581', '584', '587', '604', '613', '639',
  '647', '672', '683', '705', '709', '742', '753', '778', '780', '782',
  '807', '819', '825', '867', '873', '879', '902', '905',
])

// Non-US/CA NANP territories (Caribbean + Pacific), area code → ISO2.
const NANP_TERRITORY_AREA_CODES: Record<string, string> = {
  '242': 'BS', '246': 'BB', '264': 'AI', '268': 'AG', '284': 'VG',
  '340': 'VI', '345': 'KY', '441': 'BM', '473': 'GD', '649': 'TC',
  '658': 'JM', '664': 'MS', '670': 'MP', '671': 'GU', '684': 'AS',
  '721': 'SX', '758': 'LC', '767': 'DM', '784': 'VC', '787': 'PR',
  '809': 'DO', '829': 'DO', '849': 'DO', '868': 'TT', '869': 'KN',
  '876': 'JM', '939': 'PR',
}

// Primary country for shared dial codes where countries-list's alphabetical
// first match is the wrong default (e.g. +7 would otherwise resolve to KZ).
const SHARED_DIAL_CODE_DEFAULT_ISO2: Record<string, string> = {
  '7': 'RU',
  '212': 'MA',
  '358': 'FI',
  '590': 'GP',
  '599': 'CW',
}

/**
 * ISO2 for a dial code, disambiguated by the national number when the code is
 * shared. For +1 the area code decides: Canadian set → CA, NANP territory →
 * that territory, anything else (incl. no number / toll-free) → US.
 */
export function getIso2ByPhoneCode(phoneCode: string, nationalNumber?: string): string {
  const code = phoneCode.replace('+', '').trim()

  if (code === '1') {
    const areaCode = (nationalNumber ?? '').replace(/\D/g, '').slice(0, 3)
    if (CANADIAN_AREA_CODES.has(areaCode)) return 'CA'
    return NANP_TERRITORY_AREA_CODES[areaCode] ?? 'US'
  }

  const preferred = SHARED_DIAL_CODE_DEFAULT_ISO2[code]
  if (preferred) return preferred

  const country = getCountryDataList().find((c) => String(c.phone[0]) === code)
  return country?.iso2 ?? ''
}

export function getIso2ByCountryName(countryName: string): string {
  const country = getCountryDataList().find((c) => c.name === countryName)
  return country?.iso2 ?? ''
}

export interface PhoneDisplay {
  iso2: string
  display: string
  telHref: string
}

/**
 * Build the display form of a stored phone number for read-only views.
 *
 * Crew stores the dial code separately (phoneCountry + phone); passengers
 * store one combined string that may lack the +code prefix on legacy rows.
 * Legacy numbers without a resolvable code are shown as-is with no flag —
 * never guess a default country here.
 */
export function getPhoneDisplay(
  phone?: string | null,
  phoneCountry?: string | null,
): PhoneDisplay | null {
  if (!phone || !/\d/.test(phone)) return null

  let display: string
  let iso2: string

  if (phone.trim().startsWith('+')) {
    const { countryCode, number } = parsePhoneNumber(phone.trim())
    display = `${countryCode} ${number}`.trim()
    iso2 = getIso2ByPhoneCode(countryCode, number)
  } else if (phoneCountry) {
    display = `${phoneCountry} ${phone}`.trim()
    iso2 = getIso2ByPhoneCode(phoneCountry, phone)
  } else {
    display = phone
    iso2 = ''
  }

  return {
    iso2,
    display,
    telHref: `tel:${display.replace(/[^+\d]/g, '')}`,
  }
}
